import { Box, CircularProgress, Chip, Stack } from "@mui/material";
import Select from "react-select";
import { Button, Modal, Row, Col } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import DatePicker from "react-datepicker";
import { useState, useRef, useMemo, useEffect } from "react";
import { DatePickerWithValidation, formatYYYYMMDD, dateFieldConfig } from "../../../../utils/DateValidator";

import {
  fetchColumnFilterOptions,
  setDRGReportFilters,
  resetDRGFilters,
  setShowFilterModal,
  initialDRGFilters,
} from "../redux/ReportsSlice";

import { getColumnFilterEndpoints } from "../optionsConfig";
import closeIcon from "../../../../common/assets/images/x-close.png";

import { multiSelectStyles } from "../multiSelectStyle";
import { normalizeToArray } from "../hooks/useReport";
const HIDDEN_FROM_CHIPS = [
  "page_number",
  "limit",
  "sort_by",
  "sort_order",
  "search",
  "los_op",
];

const FILTER_LABELS = {
  episode_id: "Episode Identifier",
  // admission_date: "Admission Date",
  // separation_date: "Separation Date",
  admission_date_from: "Admission From",
  admission_date_to: "Admission To",
  discharge_date_from: "Discharge From",
  discharge_date_to: "Discharge To",
  speciality: "Speciality",
  principal_diagnosis_code: "PDX",
  description: "Description",
  alos: "ALOS",
  lower_bound: "Lower Bound",
  upper_bound: "Upper Bound",
  codes_assigned: "Codes Assigned",
  drg_code: "DRG Code",
  status: "Status",
  los: "LOS",
};

const LOS_OPERATORS = [
  { label: ">", value: "gt" },
  { label: "<", value: "lt" },
  { label: ">=", value: "gte" },
  { label: "<=", value: "lte" },
  { label: "=", value: "eq" },
];

const DRGReportFilter = ({ onApply }) => {
  const dispatch = useDispatch();
  const debounceRef = useRef({});
  const [searchTexts, setSearchTexts] = useState({});
  const [local, setLocal] = useState({});
  const showFilterModal = useSelector((s) => s.reports.showFilterModal);

  const drgReportFilters = useSelector(
    (s) => s.reports.appliedFilters["/drg-outlier-report"] || {},
  );

  const columnOptions = useSelector(
    (s) => s.reports.columnFilterOptions["/drg-outlier-report"] || {},
  );

  const columnConfig = getColumnFilterEndpoints("/drg-outlier-report");

  const columnKeyMap = {
    drg: "drg",
    description: "description",
    alos: "drgavlos",
    lower_bound: "lower_bound",
    upper_bound: "upper_bound",
  };


  const filterFields = [
    { label: "Episode Identifier", name: "episode_id", type: "list" },
    { label: "Admission Date From", name: "admission_date_from", type: "date" },
    { label: "Admission Date To", name: "admission_date_to", type: "date" },
    { label: "Discharge Date From", name: "separation_date_from", type: "date" },
    { label: "Discharge Date To", name: "separation_date_to", type: "date" },
    { label: "LOS", name: "los", type: "number" },
    { label: "Speciality", name: "speciality", type: "list" },
    { label: "PDx", name: "principal_diagnosis_code", type: "list" },
    { label: "DRG Description", name: "description", type: "list" },
    { label: "DRG ALOS", name: "alos", type: "list" },
    { label: "Lower Bound", name: "lower_bound", type: "list" },
    { label: "Upper Bound", name: "upper_bound", type: "list" },
    { label: "Codes Assigned", name: "codes_assigned", type: "list" },
    { label: "DRG Code", name: "drg", type: "list" },
    { label: "Status", name: "status", type: "list" },
  ];

  /* ───────── Chips Logic ───────── */

  const hasAny = useMemo(
    () =>
      Object.entries(local).some(([k, v]) => {
        if (HIDDEN_FROM_CHIPS.includes(k)) return false;
        return Array.isArray(v) ? v.length > 0 : Boolean(v);
      }),
    [local],
  );

  const chips = useMemo(
   () =>
     Object.entries(local).flatMap(([k, v]) => {
       if (HIDDEN_FROM_CHIPS.includes(k) || !v) return [];
 
       // Status chips
       if (k === "status" && Array.isArray(v)) {
         const statusData = columnOptions?.status?.data || [];
 
         return v.map((id) => {
           const matched = statusData.find(
             (item) => item.status_id === id
           );
 
           return {
             key: k,
             value: matched?.status || id,
             actualValue: id,
           };
         });
       }
 
       // Normal array chips
       if (Array.isArray(v)) {
         return v.map((val) => ({
           key: k,
           value: val,
           actualValue: val,
         }));
       }
 
       return [
         {
           key: k,
           value: v,
           actualValue: v,
         },
       ];
     }),
   [local, columnOptions],
 );

   const removeChip = ({ key, actualValue }) => {
  setLocal((prev) => {
    const current = prev[key];

    if (Array.isArray(current)) {
      return {
        ...prev,
        [key]: current.filter((v) => v !== actualValue),
      };
    }

    return {
      ...prev,
      [key]: null,
    };
  });
};

  /* ───────── Handlers ───────── */

  const handleChange = (name, value) => {
    setLocal((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleApplyFilters = () => {
    Object.entries(local).forEach(([key, value]) => {
      dispatch(setDRGReportFilters({ key, value }));
    });

    onApply(local);
    dispatch(setShowFilterModal(false));
  };

  // const handleResetFilters = () => {
  //   dispatch(resetDRGFilters());
  //   onApply(initialDRGFilters);
  //   dispatch(setShowFilterModal(false));
  // };

  const mapToSelectOptions = (arr, fieldName) =>
  Array.isArray(arr)
    ? arr.map((i) => {
        // status api object
        if (fieldName === "status" && typeof i === "object") {
          return {
            label: i.status,
            value: i.status_id,
          };
        }

        // generic object
        if (typeof i === "object") {
          return {
            label: i.label || i.name || i.status || i.value,
            value: i.id || i.status_id || i.value,
          };
        }

        // string array
        return {
          label: i,
          value: i,
        };
      })
    : [];

  const getOptionsWithLoader = (fieldName) => {
    const columnState = columnOptions?.[fieldName];
    const columnData = columnState?.data || [];
    const loading = columnState?.loading;

    const baseOptions = mapToSelectOptions(columnData,fieldName);

    if (loading) {
      return [
        ...baseOptions,
        {
          value: "loading",
          label: (
            <div
              style={{ display: "flex", justifyContent: "center", padding: 8 }}>
              <CircularProgress size={18} />
            </div>
          ),
          isDisabled: true,
        },
      ];
    }

    return baseOptions;
  };

  const resolveColumnKey = (fieldName) => columnKeyMap[fieldName] || fieldName;

  const dispatchFetch = (fieldName, search = "", page = 1) => {
    const columnKey = resolveColumnKey(fieldName);
    const config = columnConfig[columnKey];

    if (!config) return;

    dispatch(
      fetchColumnFilterOptions({
        reportPath: "/drg-outlier-report",
        columnKey: fieldName,
        endpoint: config.endpoint,
        responseKey: config.responseKey,
        search,
        page,
        limit: 10,
      }),
    );
  };

  const handleInputChange = (fieldName, inputValue, { action } = {}) => {
    if (action === "input-change"){
      setSearchTexts("")
    };

    setSearchTexts((prev) => ({ ...prev, [fieldName]: inputValue }));

    if (debounceRef.current[fieldName])
      clearTimeout(debounceRef.current[fieldName]);

    debounceRef.current[fieldName] = setTimeout(() => {
      dispatchFetch(fieldName, inputValue, 1);
    }, 500);
  };

  const handleMenuOpen = (fieldName) => {
    const columnState = columnOptions?.[fieldName];

    if (!columnState) {
      dispatchFetch(fieldName, "", 1);
      return;
    }

    const hasMore = columnState?.hasMore !== false;
    const count = columnState?.data?.length || 0;

    if (count < 5 && hasMore) {
      dispatchFetch(
        fieldName,
        searchTexts[fieldName] || "",
        (columnState.page || 1) + 1,
      );
    }
  };

  const handleScrollBottom = (fieldName) => {
    const columnState = columnOptions?.[fieldName];
    if (!columnState) return;
    if (columnState.loading) return;
    if (columnState.hasMore === false) return;

    dispatchFetch(
      fieldName,
      searchTexts[fieldName] || "",
      (columnState.page ?? 1) + 1,
    );
  };

  /* ───────── UI ───────── */
  useEffect(() => {
    if (showFilterModal) {
      setLocal({ ...drgReportFilters });
    }
  }, [showFilterModal, drgReportFilters]);

  useEffect(() => {
  Object.keys(local).forEach((fieldName) => {
    const columnState = columnOptions?.[fieldName];
    if (!columnState) return;

    const columnData = normalizeToArray(columnState.data);
    const selectedValues = local[fieldName] || [];

    const remaining = columnData.length - selectedValues.length;

    if (
      remaining < 5 &&
      !columnState.loading &&
      columnState.hasMore !== false
    ) {
      dispatchFetch(
        fieldName,
        searchTexts[fieldName] || "",
        (columnState.page ?? 1) + 1
      );
    }
  });
}, [local, columnOptions, searchTexts]);


  return (
    <Modal
      show={showFilterModal}
      onHide={() => dispatch(setShowFilterModal(false))}
      centered
      dialogClassName="modal-xl"
      style={{zIndex:"1500"}}>
      <Modal.Header closeButton>
        <Modal.Title>DRG Outlier Report Filters</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Box>
          {hasAny && (
            <Row className="mb-2">
              <Col className="d-flex flex-row justify-content-between align-items-start">
                <Stack direction="row" flexWrap="wrap" sx={{
                flex: 1,
                maxHeight: "80px",
                overflowY: "auto",
                overflowX: "hidden",
                alignContent: "flex-start", }}>
                  {chips.map((c, i) => (
                    <Chip
                      key={i}
                      className="m-1 bg-light"
                      size="small"
                      label={`${FILTER_LABELS[c.key] || c.key} : ${c.value}`}
                      style={{ border: "1px solid #555", fontSize: "12px" }}
                      deleteIcon={
                        <img
                          src={closeIcon}
                          alt="remove"
                          className="episodeListFilterObj-close-icon-him"
                        />
                      }
                      onDelete={() => removeChip(c)}
                    />
                  ))}
                </Stack>

                <Button
                  className="episodelist-reset-btn flex-shrink-0 ms-2"
                  onClick={() => setLocal({...initialDRGFilters})}>
                  Clear All
                </Button>
              </Col>

              <hr className="mt-3" />
            </Row>
          )}

          <Row className="g-3">
            {filterFields.map((field) => (
              <Col xs={12} sm={6} md={4} key={field.name}>
                <label className="workflow-outstanding-report-filter-label">{field.label}</label>

                {/* {field.type === "date" && (
                  <DatePicker
                    selected={
                      local[field.name] ? new Date(local[field.name]) : null
                    }
                    onChange={(date) =>
                      setLocal((prev) => ({
                        ...prev,
                        [field.name]: date
                          ? date.toISOString().split("T")[0]
                          : null,
                      }))
                    }
                    placeholderText={field.label}
                    className="workflow-him-filter-date-picker"
                    dateFormat="dd-MM-yyyy"
                    isClearable
                  />
                )} */}

                {DatePickerWithValidation(field, local, setLocal)}
                {field.type === "number" && field.name === "los" && (
                  <div style={{ display: "flex", gap: 8 }}>
                    <select
                      className="form-control"
                      style={{ width: 90 }}
                      value={local.los_op || ""}
                      onChange={(e) =>
                        setLocal((prev) => ({
                          ...prev,
                          los_op: e.target.value,
                          los: null,
                        }))
                      }>
                      <option value="">Operator</option>
                      {LOS_OPERATORS.map((operator) => (
                        <option key={operator.value} value={operator.value}>
                          {operator.label}
                        </option>
                      ))}
                    </select>

                    <input
                      type="number"
                      className="form-control"
                      placeholder="Enter LOS"
                      disabled={!local.los_op}
                      value={local.los ?? ""}
                      // onChange={(e) =>
                      //   setLocal((prev) => ({
                      //     ...prev,
                      //     los: e.target.value ? Number(e.target.value) : null,
                      //   }))
                      // }
                      onChange={(e) =>
                        setLocal((prev) => ({
                               ...prev,
                        los: e.target.value || "",
                        }))
                       }
                    />
                  </div>
                )}
                {field.type === "list" && (
                  <Select
                    isMulti
                    className="filter-model-input-option-field"
                    styles={multiSelectStyles}
                    options={getOptionsWithLoader(field.name)}
                    // value={
                    //   Array.isArray(local[field.name])
                    //     ? mapToSelectOptions(local[field.name])
                    //     : []
                    // }
                      value={
                           Array.isArray(local[field.name])
                           ? field.name === "status"
                           ? getOptionsWithLoader(field.name).filter((option) =>
                           local[field.name].includes(option.value)
                           )
                          : mapToSelectOptions(local[field.name], field.name)
                           : []
                            }
                    closeMenuOnSelect={false}
                    placeholder={`Select ${field.label}`}
                    isOptionDisabled={(opt) => opt.value === "loading"}
                    onMenuOpen={() => handleMenuOpen(field.name)}
                    onMenuScrollToBottom={() => handleScrollBottom(field.name)}
                    onInputChange={(inputValue, actionMeta) =>
                      handleInputChange(field.name, inputValue, actionMeta)
                    }
                    onChange={(selectedOptions) =>
                      handleChange(
                        field.name,
                        selectedOptions
                          ? selectedOptions.map((opt) => opt.value)
                          : [],
                      )
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      e.stopPropagation();
                    }
                    }}

                  />
                )}
                 {field.type === "text" && (
                  <input
                    type="text"
                    classNamePrefix="react-select"
                    className="form-control"
                    value={local[field.name] || ""}
                    onChange={(e) =>
                      setLocal((prev) => ({
                        ...prev,
                        [field.name]: e.target.value,
                      }))
                    }
                    placeholder={field.label}
                  />
                )}
              </Col>
            ))}
          </Row>
        </Box>
      </Modal.Body>

      <Modal.Footer>
        {/* <Button variant="secondary" onClick={handleResetFilters}>
          Reset
        </Button> */}

        <Button
          className="el-filter-cancel btn btn-secondary"
          onClick={() => dispatch(setShowFilterModal(false))}>
          Cancel
        </Button>

        <Button className="el-filter-submit" onClick={handleApplyFilters}>
          Apply
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DRGReportFilter;
